import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Key,
  User,
  Settings,
  LogOut,
  Menu,
  X,
  Lock,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Super admins com acesso total (sempre têm acesso Premium)
const SUPER_ADMIN_EMAILS = ['lu.756@icloud.com', 'lucienne.escritora@gmail.com'];

// Custom Icon Component - loads images from /icons folder
const CustomIcon = ({ name, size = 40, className = "" }) => (
  <img 
    src={`/icons/${name}.png`} 
    alt={name}
    width={size}
    height={size}
    className={`object-contain ${className}`}
    style={{ minWidth: size, minHeight: size }}
  />
);

// Elegant color palette for books
const bookColors = [
  '#57534e', // stone-600 (grafite suave)
  '#92400e', // amber-800 (sépia)
  '#b45309', // amber-700 (dourado suave)
  '#78716c', // stone-500 (marfim escuro)
  '#854d0e', // yellow-800 (âmbar antigo)
];

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [booksInProgress, setBooksInProgress] = useState([]);

  // Verificar se usuário tem acesso Premium (apenas super admins por enquanto)
  const userEmail = user?.email?.toLowerCase() || '';
  const isSuperAdmin = SUPER_ADMIN_EMAILS.some(email => email.toLowerCase() === userEmail);
  const isPremium = isSuperAdmin; // Por enquanto, apenas super admins têm acesso

  // Handler para clique em menu Premium bloqueado
  const handlePremiumClick = (e, menuName) => {
    if (!isPremium) {
      e.preventDefault();
      e.stopPropagation();
      navigate('/upgrade', { state: { feature: menuName } });
    }
  };

  useEffect(() => {
    fetchBooksInProgress();
  }, []);

  const fetchBooksInProgress = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/books/in-progress`, {
        withCredentials: true
      });
      setBooksInProgress(response.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching books:', error);
    }
  };

  // Get last edited chapter for a book
  const getLastChapter = (bookId) => {
    return localStorage.getItem(`lastChapter_${bookId}`);
  };

  // Navigate to continue writing
  const handleContinueWriting = async (bookId) => {
    const lastChapterId = getLastChapter(bookId);
    
    if (lastChapterId) {
      navigate(`/editor/${lastChapterId}`);
    } else {
      // If no last chapter, get the first chapter
      try {
        const response = await axios.get(`${API_URL}/api/books/${bookId}/chapters`, {
          withCredentials: true
        });
        if (response.data && response.data.length > 0) {
          navigate(`/editor/${response.data[0].chapter_id}`);
        } else {
          // No chapters, go to book structure
          navigate(`/livro/${bookId}`);
        }
      } catch (error) {
        navigate(`/livro/${bookId}`);
      }
    }
    setMobileMenuOpen(false);
  };

  // Get color for book based on index
  const getBookColor = (index) => {
    return bookColors[index % bookColors.length];
  };

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="fixed top-4 left-4 z-50 md:hidden p-2 rounded-lg bg-card border border-border"
        data-testid="mobile-menu-toggle"
      >
        {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <aside 
        className={`
          fixed inset-y-0 left-0 z-40 w-64 bg-card border-r border-border/40 flex flex-col
          transform transition-transform duration-200 ease-in-out
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0
        `}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="p-6 border-b border-border/40">
          <Link to="/entrada" className="flex items-center gap-2">
            <Key className="w-8 h-8 text-amber-500" />
            <span className="text-xl font-serif">Author's Key</span>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto">
          <TooltipProvider>
            <ul className="space-y-1">
              {/* Meus Livros */}
              <li>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to="/meus-livros"
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-2 px-4 py-3 rounded-md transition-colors ${
                        location.pathname === '/meus-livros'
                          ? 'bg-amber-500/15 text-amber-700 dark:text-amber-500'
                          : 'text-muted-foreground hover:bg-amber-500/10 hover:text-amber-700 dark:hover:text-amber-500'
                      }`}
                      data-testid="menu-meus-livros"
                    >
                      <CustomIcon name="livro-aberto" size={48} />
                      <span className="text-sm font-medium">Meus Livros</span>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="bg-stone-800 text-stone-100 border-stone-700">
                    <p>Contemple suas obras já criadas</p>
                  </TooltipContent>
                </Tooltip>
              </li>

              {/* Criar Novo Livro */}
              <li>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to="/novo-livro"
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-2 px-4 py-3 rounded-md transition-colors ${
                        location.pathname === '/novo-livro'
                          ? 'bg-amber-500/15 text-amber-700 dark:text-amber-500'
                          : 'text-muted-foreground hover:bg-amber-500/10 hover:text-amber-700 dark:hover:text-amber-500'
                      }`}
                      data-testid="menu-criar-novo-livro"
                    >
                      <CustomIcon name="tinteiro-pena" size={62} />
                      <span className="text-sm font-medium">Criar Novo Livro</span>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="bg-stone-800 text-stone-100 border-stone-700">
                    <p>Dê vida à sua nova ideia</p>
                  </TooltipContent>
                </Tooltip>
              </li>

              {/* Divisor */}
              <li className="my-3">
                <div className="border-t border-border/40"></div>
              </li>
            </ul>

            {/* Bloco Dinâmico: Escrevendo... - DESTACADO */}
            <div className="mb-6 mx-2 p-3 bg-amber-500/5 dark:bg-amber-900/10 rounded-lg border border-amber-500/10">
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="px-2 text-sm font-semibold text-amber-700 dark:text-amber-500 uppercase tracking-wide mb-3 cursor-help flex items-center gap-1">
                    <CustomIcon name="pena" size={36} />
                    Escrevendo...
                  </p>
                </TooltipTrigger>
                <TooltipContent side="right" className="bg-stone-800 text-stone-100 border-stone-700">
                  <p>Continue de onde parou</p>
                </TooltipContent>
              </Tooltip>
              
              {booksInProgress.length > 0 ? (
                <ul className="space-y-2 max-h-48 overflow-y-auto">
                  {booksInProgress.map((book, index) => {
                    const isBookActive = location.pathname === `/livro/${book.book_id}`;
                    
                    return (
                      <li key={book.book_id}>
                        <button
                          onClick={() => handleContinueWriting(book.book_id)}
                          className={`flex items-center gap-2 pl-5 pr-3 py-2.5 rounded-md transition-all text-sm w-full text-left ${
                            isBookActive
                              ? 'bg-amber-500/20 text-amber-700 dark:text-amber-400'
                              : 'text-foreground/80 hover:bg-amber-500/10 hover:text-amber-700 dark:hover:text-amber-400 hover:translate-x-0.5'
                          }`}
                          data-testid={`escrevendo-${book.book_id}`}
                        >
                          <CustomIcon name="pena" size={30} />
                          <span className="truncate flex-1">{book.title}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="pl-5 text-xs text-muted-foreground italic">Nenhum livro em andamento</p>
              )}
            </div>

            {/* Espaço respiro após Escrevendo */}
            <div className="my-5 border-t border-border/30"></div>

            <ul className="space-y-2">
              {/* Cafeteria Best Seller - PREMIUM */}
              <li>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to={isPremium ? "/cafeteria" : "#"}
                      onClick={(e) => {
                        if (!isPremium) {
                          handlePremiumClick(e, 'Cafeteria Best Seller');
                        } else {
                          setMobileMenuOpen(false);
                        }
                      }}
                      className={`flex items-center gap-2 px-4 py-3 rounded-md transition-colors ${
                        !isPremium 
                          ? 'opacity-50 cursor-not-allowed'
                          : location.pathname === '/cafeteria'
                            ? 'bg-amber-500/15 text-amber-700 dark:text-amber-500'
                            : 'text-muted-foreground hover:bg-amber-500/10 hover:text-amber-700 dark:hover:text-amber-500'
                      }`}
                      data-testid="menu-cafeteria"
                    >
                      <CustomIcon name="xicara" size={48} />
                      <span className="text-sm font-medium">Cafeteria Best Seller</span>
                      {!isPremium && <Lock size={12} className="ml-1 text-stone-500" />}
                      <span className="ml-auto text-[6px] bg-amber-500/80 text-stone-900 px-1 py-0.5 rounded-full">Premium</span>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="bg-stone-800 text-stone-100 border-stone-700">
                    <p>{isPremium ? 'Travou? Converse e inspire-se com o conselho literário' : 'Recurso Premium - Faça upgrade para desbloquear'}</p>
                  </TooltipContent>
                </Tooltip>
              </li>

              {/* Progresso Criativo */}
              <li>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to="/dashboard"
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-2 px-4 py-3 rounded-md transition-colors ${
                        location.pathname === '/dashboard'
                          ? 'bg-amber-500/15 text-amber-700 dark:text-amber-500'
                          : 'text-muted-foreground hover:bg-amber-500/10 hover:text-amber-700 dark:hover:text-amber-500'
                      }`}
                      data-testid="menu-progresso-criativo"
                    >
                      <CustomIcon name="grafico" size={62} />
                      <span className="text-sm font-medium">Progresso Criativo</span>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="bg-stone-800 text-stone-100 border-stone-700">
                    <p>Acompanhe sua evolução e metas</p>
                  </TooltipContent>
                </Tooltip>
              </li>

              {/* Baú Intelectual */}
              <li>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to="/bau-intelectual"
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-2 px-4 py-3 rounded-md transition-colors ${
                        location.pathname === '/bau-intelectual' || location.pathname === '/arquivo-intelectual'
                          ? 'bg-amber-500/15 text-amber-700 dark:text-amber-500'
                          : 'text-muted-foreground hover:bg-amber-500/10 hover:text-amber-700 dark:hover:text-amber-500'
                      }`}
                      data-testid="menu-bau-intelectual"
                    >
                      <CustomIcon name="bau" size={48} />
                      <span className="text-sm font-medium">Baú Intelectual</span>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="bg-stone-800 text-stone-100 border-stone-700">
                    <p>Guarde manuscritos, ideias, frases e inspirações</p>
                  </TooltipContent>
                </Tooltip>
              </li>

              {/* Divisor + Respiro antes da Camada Premium */}
              <li className="my-5">
                <div className="border-t border-border/30"></div>
              </li>

              {/* CAMADA PREMIUM - Time Ativo + Emulador + Empresário */}
              {/* Time Ativo - PREMIUM */}
              <li className="px-3 py-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div 
                      onClick={(e) => {
                        if (!isPremium) {
                          handlePremiumClick(e, 'Time Literário Ativo');
                        }
                      }}
                      className={`hover:scale-[1.02] transition-transform ${!isPremium ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20">
                        <div className="relative">
                          <CustomIcon name="ampulheta" size={42} />
                          {!isPremium && <Lock size={12} className="absolute -top-1 -right-1 text-stone-500" />}
                        </div>
                        <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
                          Time Ativo
                        </span>
                        <span className="ml-auto text-[6px] bg-amber-500/80 text-stone-900 px-1 py-0.5 rounded-full">Premium</span>
                      </div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="bg-stone-800 text-stone-100 border-stone-700">
                    <p>{isPremium ? 'Ative seu modo de escrita avançado' : 'Recurso Premium - Faça upgrade para desbloquear'}</p>
                  </TooltipContent>
                </Tooltip>
              </li>

              {/* Emulador Acelerador - PREMIUM */}
              <li>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to={isPremium ? "/emulador-acelerador" : "#"}
                      onClick={(e) => {
                        if (!isPremium) {
                          handlePremiumClick(e, 'Emulador Acelerador');
                        } else {
                          setMobileMenuOpen(false);
                        }
                      }}
                      className={`flex items-center gap-1 px-4 py-2.5 rounded-md transition-colors ${
                        !isPremium 
                          ? 'opacity-50 cursor-not-allowed'
                          : location.pathname === '/emulador-acelerador'
                            ? 'bg-amber-500/15 text-amber-700 dark:text-amber-500'
                            : 'text-muted-foreground hover:bg-amber-500/10 hover:text-amber-700 dark:hover:text-amber-500'
                      }`}
                      data-testid="menu-emulador-acelerador"
                    >
                      <CustomIcon name="chapeu-magico" size={55} />
                      <span className="text-sm font-medium">Emulador Acelerador</span>
                      {!isPremium && <Lock size={12} className="ml-1 text-stone-500" />}
                      <span className="ml-auto text-[6px] bg-amber-500/80 text-stone-900 px-1 py-0.5 rounded-full">Premium</span>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="bg-stone-800 text-stone-100 border-stone-700">
                    <p>{isPremium ? 'Escreva com apoio inteligente' : 'Recurso Premium - Faça upgrade para desbloquear'}</p>
                  </TooltipContent>
                </Tooltip>
              </li>

              {/* Empresário Literário - PREMIUM */}
              <li>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to={isPremium ? "/empresario-literario" : "#"}
                      onClick={(e) => {
                        if (!isPremium) {
                          handlePremiumClick(e, 'Empresário Literário');
                        } else {
                          setMobileMenuOpen(false);
                        }
                      }}
                      className={`flex items-center gap-1 px-4 py-2.5 rounded-md transition-colors ${
                        !isPremium 
                          ? 'opacity-50 cursor-not-allowed'
                          : location.pathname === '/empresario-literario'
                            ? 'bg-amber-500/15 text-amber-700 dark:text-amber-500'
                            : 'text-muted-foreground hover:bg-amber-500/10 hover:text-amber-700 dark:hover:text-amber-500'
                      }`}
                      data-testid="menu-empresario-literario"
                    >
                      <CustomIcon name="escudo" size={55} />
                      <span className="text-sm font-medium">Empresário Literário</span>
                      {!isPremium && <Lock size={12} className="ml-1 text-stone-500" />}
                      <span className="ml-auto text-[6px] bg-amber-500/80 text-stone-900 px-1 py-0.5 rounded-full">Premium</span>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="bg-stone-800 text-stone-100 border-stone-700">
                    <p>{isPremium ? 'Transforme sua escrita em valor e alcance leitores' : 'Recurso Premium - Faça upgrade para desbloquear'}</p>
                  </TooltipContent>
                </Tooltip>
              </li>

              {/* Divisor antes do rodapé */}
              <li className="my-3">
                <div className="border-t border-border/30"></div>
              </li>
            </ul>
          </TooltipProvider>
        </nav>

        {/* Rodapé: Segurança + Modo + Perfil */}
        <div className="px-4 pb-3 pt-2 border-t border-border/40 relative">
          {/* Segurança */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to="/seguranca"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-2 w-full rounded-md px-3 py-1.5 transition-colors ${
                    location.pathname === '/seguranca'
                      ? 'bg-amber-500/15 text-amber-700 dark:text-amber-500'
                      : 'text-muted-foreground hover:bg-amber-500/10 hover:text-amber-700 dark:hover:text-amber-500'
                  }`}
                  data-testid="menu-seguranca"
                >
                  <CustomIcon name="escudo" size={30} />
                  <span className="text-xs">Segurança</span>
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right" className="bg-stone-800 text-stone-100 border-stone-700">
                <p>Proteja e valide sua autoria</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="flex items-center gap-2 w-full hover:bg-amber-500/10 hover:text-amber-700 dark:hover:text-amber-400 rounded-md px-3 py-1.5 transition-colors text-muted-foreground"
            data-testid="theme-toggle-sidebar"
          >
            <CustomIcon name="sol" size={30} />
            <span className="text-xs">{theme === 'dark' ? 'Modo Claro' : 'Modo Escuro'}</span>
          </button>

          {/* User Avatar Button */}
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-3 w-full hover:bg-amber-500/10 rounded-md p-2 mt-1 transition-colors"
            data-testid="user-menu-trigger"
          >
            {user?.picture ? (
              <img 
                src={user.picture} 
                alt={user.name} 
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                <User size={20} className="text-amber-600" />
              </div>
            )}
            <div className="flex-1 text-left">
              <p className="text-[10px] font-medium truncate">{user?.name || 'Autor'}</p>
              <p className="text-[8px] text-muted-foreground truncate">{user?.email}</p>
            </div>
          </button>

          {/* User Dropdown */}
          {showUserMenu && (
            <div className="absolute bottom-full left-4 right-4 mb-2 bg-popover border border-border rounded-md shadow-lg overflow-hidden">
              <Link
                to="/perfil-do-autor"
                onClick={() => {
                  setShowUserMenu(false);
                  setMobileMenuOpen(false);
                }}
                className="flex items-center gap-2 px-4 py-3 hover:bg-amber-500/10 hover:text-amber-700 transition-colors border-b border-border/40"
                data-testid="menu-perfil-do-autor"
              >
                <User size={18} />
                <span>Perfil do Autor</span>
              </Link>
              <Link
                to="/configuracoes"
                onClick={() => {
                  setShowUserMenu(false);
                  setMobileMenuOpen(false);
                }}
                className="flex items-center gap-2 px-4 py-3 hover:bg-amber-500/10 hover:text-amber-700 transition-colors border-b border-border/40"
                data-testid="menu-configuracoes"
              >
                <Settings size={18} />
                <span>Configurações</span>
              </Link>
              <button
                onClick={() => {
                  setShowUserMenu(false);
                  logout();
                }}
                className="flex items-center gap-2 px-4 py-3 hover:bg-red-500/10 transition-colors w-full text-left text-destructive"
                data-testid="logout-button"
              >
                <LogOut size={18} />
                <span>Sair</span>
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Overlay for mobile */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </>
  );
}
